import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { OpeningHour } from '../state/types';
import { iconChat, iconClock, iconInfo } from './icons';

/**
 * <embrace-status-hero> — generieke "status"-content in het body-area:
 *   - offline  : "chat is nu gesloten" + openingstijden-tabel
 *   - queue    : "je staat in de wachtrij" + progressbar
 *   - ended    : "gesprek beëindigd" placeholder
 */
export type HeroMode = 'offline' | 'queue' | 'ended';

@customElement('embrace-status-hero')
export class EmbraceStatusHero extends LitElement {
  @property({ type: String }) mode: HeroMode = 'offline';
  @property({ attribute: false }) openingHours: OpeningHour[] | null = null;
  @property({ type: Number }) todayIndex = 0;
  @property({ type: String }) contactEmail = '';
  @property({ type: Number }) queuePosition = 0;
  @property({ type: Number }) queueWaitMin = 0;

  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    .hero {
      padding: 28px 22px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 14px;
      text-align: center;
      background: var(--ec-background);
      flex: 1;
      min-height: 0;
    }

    .ic-wrap {
      width: 88px;
      height: 88px;
      border-radius: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .ic-wrap.closed { background: var(--ec-warning-subtle); color: var(--ec-warning); }
    .ic-wrap.waiting { background: #EBF0FC; color: #0C57E1; }
    .ic-wrap.ended { background: var(--ec-surface-sunken); color: #5E5F62; }

    h3 {
      margin: 0;
      font: 700 18px/1.25 var(--ec-font-display);
      color: var(--ec-text);
    }
    p {
      margin: 0;
      font: 400 14px/1.5 var(--ec-font-family);
      color: #5E5F62;
      max-width: 36ch;
    }

    /* Openingstijden-tabel */
    .hours-table {
      margin-top: 8px;
      width: 100%;
      max-width: 300px;
      border: 1px solid var(--ec-border);
      border-radius: 14px;
      background: #fff;
      padding: 4px 0;
      font: 500 13px/1.4 var(--ec-font-family);
    }
    .hours-table .row {
      display: flex;
      align-items: center;
      padding: 8px 16px;
    }
    .hours-table .row + .row {
      border-top: 1px solid var(--ec-border);
    }
    .hours-table .row .d {
      color: var(--ec-text);
      font-weight: 600;
    }
    .hours-table .row .h {
      margin-left: auto;
      color: #5E5F62;
    }
    .hours-table .row.today {
      background: var(--ec-warning-subtle);
    }
    .hours-table .row.today .d,
    .hours-table .row.today .h {
      color: var(--ec-warning);
    }
    .hours-table .row.today .h { font-weight: 700; }
    .hours-table .row.closed .h { color: #B91C1C; }

    .contact-email {
      margin-top: 6px;
      font: 500 13px/1.4 var(--ec-font-family);
      color: #5E5F62;
    }
    .contact-email a {
      color: var(--ec-primary);
      text-decoration: none;
      font-weight: 600;
    }
    .contact-email a:hover { text-decoration: underline; }

    .queue-bar {
      width: 100%;
      max-width: 240px;
      height: 4px;
      background: #EBF0FC;
      border-radius: 2px;
      overflow: hidden;
      margin-top: 8px;
    }
    .queue-bar span {
      display: block;
      height: 100%;
      background: linear-gradient(90deg, #4175E6, #0C57E1);
      animation: queueSlide 1.8s infinite ease-in-out;
      width: 40%;
    }
    @keyframes queueSlide {
      0%   { transform: translateX(-100%); }
      100% { transform: translateX(260%); }
    }
  `;

  override render() {
    if (this.mode === 'offline') return this.renderOffline();
    if (this.mode === 'queue') return this.renderQueue();
    return this.renderEnded();
  }

  private renderOffline() {
    return html`
      <div class="hero" role="status" aria-live="polite">
        <div class="ic-wrap closed" aria-hidden="true">
          ${iconClock()}
        </div>
        <h3>Chat is nu gesloten</h3>
        <p>Je kunt ons via webchat bereiken op onderstaande tijden.</p>
        ${this.renderHoursTable()}
        ${this.contactEmail
          ? html`
              <p class="contact-email">
                Of stuur ons een e-mail:<br />
                <a href=${`mailto:${this.contactEmail}`}>${this.contactEmail}</a>
              </p>
            `
          : nothing}
      </div>
    `;
  }

  private renderQueue() {
    return html`
      <div class="hero" role="status" aria-live="polite">
        <div class="ic-wrap waiting" aria-hidden="true">
          ${iconChat()}
        </div>
        <h3>Je staat in de wachtrij</h3>
        <p>Een medewerker is binnen enkele minuten bij je.${this.queueWaitMin
          ? html` Geschatte wachttijd: ±${this.queueWaitMin} min.`
          : nothing}</p>
        <div class="queue-bar"><span></span></div>
      </div>
    `;
  }

  private renderEnded() {
    return html`
      <div class="hero" role="status" aria-live="polite">
        <div class="ic-wrap ended" aria-hidden="true">
          ${iconInfo()}
        </div>
        <h3>Gesprek beëindigd</h3>
        <p>Je kunt hierboven het transcript downloaden of het gesprek sluiten.</p>
      </div>
    `;
  }

  private renderHoursTable() {
    const hours = this.openingHours;
    if (!hours || hours.length === 0) return nothing;

    // Groepeer opeenvolgende gesloten dagen
    type Row = OpeningHour & { idxs: number[]; d: string };
    const rows: Row[] = [];
    for (let i = 0; i < hours.length; i++) {
      const r = hours[i]!;
      const next = hours[i + 1];
      if (r.closed && next?.closed && r.hours === next.hours) {
        rows.push({ ...r, d: `${r.label} & ${next.label}`, idxs: [i, i + 1] });
        i++;
      } else {
        rows.push({ ...r, d: r.label, idxs: [i] });
      }
    }

    return html`
      <div class="hours-table" part="hours-table">
        ${rows.map(
          (r) => html`
            <div class=${this.rowClass(r)}>
              <span class="d">${r.d}${r.idxs.includes(this.todayIndex) ? ' (vandaag)' : ''}</span>
              <span class="h">${r.hours}</span>
            </div>
          `,
        )}
      </div>
    `;
  }

  private rowClass(r: { idxs: number[]; closed?: boolean }): string {
    const parts = ['row'];
    if (r.idxs.includes(this.todayIndex)) parts.push('today');
    if (r.closed) parts.push('closed');
    return parts.join(' ');
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-status-hero': EmbraceStatusHero;
  }
}
